<template>
  <b-modal
    id="view_nfse"
    v-model="show"
    title="Detalhe da nota fiscal"
    hide-footer
    size="lg"
    @hidden="$emit('close')"
    no-close-on-backdrop
  >
    <div v-if="nfse" class="nfse-detail">
      <ErrorsMessages :errors="errors" />

      <div class="detail-row">
        <div class="detail-item">
          <span class="detail-label">Status</span>
          <span class="status-badge" :class="statusClass(nfse.status)">
            {{ statusLabel(nfse.status) }}
          </span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Valor</span>
          <span class="detail-value">{{
            formatCurrency(nfse.service_value)
          }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Residente</span>
          <span class="detail-value">{{ nfse.resident_name || "—" }}</span>
        </div>
      </div>

      <div class="detail-row">
        <div class="detail-item">
          <span class="detail-label">Código do serviço</span>
          <span class="detail-value">{{ nfse.service_code }}</span>
        </div>
        <div class="detail-item detail-item-wide">
          <span class="detail-label">Descrição</span>
          <span class="detail-value">{{ nfse.service_description }}</span>
        </div>
      </div>

      <div v-if="nfse.error_message" class="alert-box">
        <i class="fas fa-exclamation-triangle mr-2"></i>
        {{ nfse.error_message }}
      </div>

      <div v-if="nfse.cancel_reason" class="alert-box alert-box-neutral">
        <i class="fas fa-ban mr-2"></i>
        Cancelada: {{ nfse.cancel_reason }}
      </div>

      <!-- Histórico de eventos -->
      <div class="section-title">Histórico</div>
      <div v-if="events.length === 0" class="no-events">
        Nenhum evento recebido ainda.
      </div>
      <div v-for="event in events" :key="event.id" class="event-row">
        <i class="fas fa-circle event-dot"></i>
        <div>
          <div class="event-type">{{ event.event_type }}</div>
          <div class="event-date">{{ formatDateTime(event.received_at) }}</div>
        </div>
      </div>

      <!-- Cancelamento -->
      <div v-if="canCancel" class="cancel-section">
        <div class="section-title">Cancelar nota</div>
        <v-textarea
          v-model="cancelReason"
          placeholder="Motivo do cancelamento (mínimo 15 caracteres)"
          outlined
          dense
          hide-details
          rows="3"
          class="modern-input"
        />
        <div class="form-actions">
          <v-btn
            class="btn-danger-modern"
            :loading="cancelling"
            :disabled="cancelReason.length < 15"
            @click="handleCancel"
          >
            <i class="fas fa-ban mr-2"></i>
            Confirmar cancelamento
          </v-btn>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import ErrorsMessages from "../Components/ErrorsMessages";
import api from "../../services/api";
import permission from "../../lib/permission";

export default {
  name: "ViewNfse",
  components: { ErrorsMessages },
  props: {
    nfse: { type: Object, default: null },
  },
  data() {
    return {
      show: false,
      errors: [],
      events: [],
      cancelReason: "",
      cancelling: false,
    };
  },
  computed: {
    canCancel() {
      return (
        permission("write", "nfse") &&
        this.nfse &&
        ["processing", "authorized"].includes(this.nfse.status)
      );
    },
  },
  watch: {
    nfse(value) {
      this.show = !!value;
      this.cancelReason = "";
      this.errors = [];
      if (value) this.loadDetail(value.id);
    },
  },
  methods: {
    statusLabel(status) {
      const labels = {
        draft: "Rascunho",
        processing: "Processando",
        authorized: "Autorizada",
        rejected: "Rejeitada",
        cancelled: "Cancelada",
      };
      return labels[status] || status;
    },

    statusClass(status) {
      const map = {
        draft: "status-draft",
        processing: "status-processing",
        authorized: "status-authorized",
        rejected: "status-rejected",
        cancelled: "status-cancelled",
      };
      return map[status] || "status-draft";
    },

    formatCurrency(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },

    formatDateTime(value) {
      if (!value) return "";
      return new Date(value).toLocaleString("pt-BR");
    },

    async loadDetail(id) {
      try {
        const response = await api.get("/rest-home/nfse", { params: { id } });
        const { error, events } = response.data;
        if (!error) this.events = events || [];
      } catch (error) {
        console.log(error);
      }
    },

    async handleCancel() {
      try {
        this.cancelling = true;

        const response = await api.post("/rest-home/nfse/cancel", {
          id: this.nfse.id,
          reason: this.cancelReason,
        });

        const { error, errors } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.$emit("updated");
          this.show = false;
        }

        this.cancelling = false;
      } catch (error) {
        console.log(error);
        this.cancelling = false;
      }
    },
  },
};
</script>

<style scoped>
.nfse-detail {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-item-wide {
  grid-column: span 2;
}

.detail-label {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  color: #1b4965;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e5e7eb;
}

.no-events {
  font-size: 13px;
  color: #94a3b8;
}

.event-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px 0;
}

.event-dot {
  font-size: 8px;
  color: #1b4965;
  margin-top: 6px;
}

.event-type {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

.event-date {
  font-size: 12px;
  color: #94a3b8;
}

.alert-box {
  padding: 12px 16px;
  background: #fdf1e7;
  border: 1px solid #c9702b;
  border-radius: 10px;
  color: #9a5420;
  font-size: 13px;
  font-weight: 500;
}

.alert-box-neutral {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #475569;
}

.cancel-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.btn-danger-modern {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  width: fit-content;
}

.status-draft {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.status-processing {
  background: #fef9c3;
  color: #a16207;
  border: 1px solid #facc15;
}

.status-authorized {
  background: #d1fae5;
  color: #059669;
  border: 1px solid #34d399;
}

.status-rejected {
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #f87171;
}

.status-cancelled {
  background: #e5e7eb;
  color: #4b5563;
  border: 1px solid #9ca3af;
}
</style>